import { Link } from "react-router-dom";
import { Droplets, Trash2, Home as HomeIcon, ArrowRight, CheckCircle2 } from "lucide-react";

const BIN = "https://images.pexels.com/photos/33059897/pexels-photo-33059897.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const WASH = "https://images.pexels.com/photos/4876639/pexels-photo-4876639.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function PageHead({ eyebrow, title, sub }) {
  return (
    <div className="mx-auto max-w-3xl px-5 pt-20 text-center lg:px-8">
      <p className="text-sm font-semibold uppercase tracking-wider text-primary">{eyebrow}</p>
      <h1 className="mt-3 font-heading text-4xl font-extrabold tracking-tighter md:text-5xl">{title}</h1>
      <p className="mt-5 text-lg text-muted-foreground">{sub}</p>
    </div>
  );
}

export default function Services() {
  return (
    <div data-testid="services-page" className="pb-24">
      <PageHead
        eyebrow="Our Services"
        title="Bin cleaning & pressure washing, done right"
        sub="Two services, one goal: a cleaner, fresher-smelling home exterior with zero effort on your part."
      />
      <div className="mx-auto mt-16 max-w-6xl space-y-16 px-5 lg:px-8">
        {/* Bin cleaning */}
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <img src={BIN} alt="Clean green bin" className="h-80 w-full rounded-2xl object-cover" />
          <div>
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground">
              <Trash2 className="h-6 w-6" />
            </span>
            <h2 className="mt-5 font-heading text-3xl font-bold tracking-tight">Curbside Bin Cleaning</h2>
            <p className="mt-4 text-muted-foreground">
              Your trash and recycling bins collect months of rot, maggots, mold and
              stubborn odor. We roll up to your curb after pickup day and blast them
              inside and out with 200°F pressurized water, then sanitize and deodorize.
            </p>
            <ul className="mt-6 space-y-2 text-sm">
              {["Hot high-pressure wash", "Eco-friendly, biodegradable soap", "Sanitized & deodorized", "Wastewater fully contained"].map((f) => (
                <li key={f} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> {f}</li>
              ))}
            </ul>
          </div>
        </div>
        {/* Pressure washing */}
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground">
              <Droplets className="h-6 w-6" />
            </span>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <h2 className="font-heading text-3xl font-bold tracking-tight">Pressure Washing</h2>
              <span data-testid="pressure-coming-soon" className="rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wider text-accent-foreground">Coming Soon</span>
            </div>
            <p className="mt-4 text-muted-foreground">
              Beyond bins, we tackle grimy driveways, walkways, patios, roofs and
              trash-can corrals. Ask us when you book — we'll bring the right nozzle for the job.
            </p>
            <ul className="mt-6 space-y-2 text-sm">
              {["Roof cleaning", "Driveways & walkways", "Patios & porches", "Trash corrals & pads", "Surface-safe pressure"].map((f) => (
                <li key={f} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> {f}</li>
              ))}
            </ul>
          </div>
          <img src={WASH} alt="Pressure washing" className="order-1 h-80 w-full rounded-2xl object-cover lg:order-2" />
        </div>
      </div>

      <div className="mx-auto mt-20 max-w-3xl rounded-2xl border border-primary/30 bg-gradient-to-br from-primary via-primary/80 to-[#0D1B2A] px-8 py-10 text-center ring-1 ring-secondary/20">
        <h3 className="font-heading text-2xl font-bold text-white">
          Let's get your bins renewed
        </h3>
        <p className="mt-3 text-white/85">Book a plan or a one-time deep clean in under a minute.</p>
        <div className="mx-auto mt-6 flex max-w-md justify-center px-5">
          <Link to="/booking" className="inline-flex items-center gap-2 rounded-xl bg-secondary px-7 py-3.5 font-extrabold text-secondary-foreground shadow-[0_0_20px_rgba(57,255,20,0.3)] transition-all hover:-translate-y-0.5 hover:brightness-110">
            Book Now <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
